import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Plus,
  Calendar,
  Music,
  Users,
  Clock,
  Trophy,
  Copy,
  Download,
  Upload,
  Trash2,
  X,
} from 'lucide-react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import Navigation from '@/components/Navigation';
import { useStore } from '@/lib/store';
import * as api from '@/lib/api';
import type { LeagueScoring } from '@/lib/mock-data';

type RoundDraft = {
  roundNumber: number;
  theme: string;
  description: string;
  season: string;
  submissionDeadline: string;
  votingDeadline: string;
};

type ScoringPreset = {
  id: string;
  label: string;
  description: string;
  minPlayers?: number;
  maxPlayers?: number;
  config: LeagueScoring;
};

const scoringPresets: ScoringPreset[] = [
  {
    id: 'under-10',
    label: 'Up to 10 players',
    description: 'Ideal for smaller, close-knit leagues.',
    maxPlayers: 10,
    config: {
      max_upvotes_per_player: 10,
      max_upvotes_per_song: 3,
      max_downvotes_per_player: 2,
      max_downvotes_per_song: 1,
    },
  },
  {
    id: '11-15',
    label: '11 – 15 players',
    description: 'Balanced scoring for mid-sized leagues.',
    minPlayers: 11,
    maxPlayers: 15,
    config: {
      max_upvotes_per_player: 15,
      max_upvotes_per_song: 4,
      max_downvotes_per_player: 3,
      max_downvotes_per_song: 1,
    },
  },
  {
    id: '16-25',
    label: '16 – 25 players',
    description: 'Encourages participation in larger groups.',
    minPlayers: 16,
    maxPlayers: 25,
    config: {
      max_upvotes_per_player: 20,
      max_upvotes_per_song: 4,
      max_downvotes_per_player: 4,
      max_downvotes_per_song: 1,
    },
  },
  {
    id: 'above-25',
    label: 'More than 25 players',
    description: 'Extra wiggle room for massive leagues.',
    minPlayers: 26,
    config: {
      max_upvotes_per_player: 25,
      max_upvotes_per_song: 5,
      max_downvotes_per_player: 5,
      max_downvotes_per_song: 1,
    },
  },
];

const steps = [
  { number: 1, title: 'Season Details', description: 'Name your season and configure settings' },
  { number: 2, title: 'Participants & Scoring', description: 'Headcount and voting rules' },
  { number: 3, title: 'Rounds', description: 'Add or import all round details' },
  { number: 4, title: 'Review', description: 'Double-check everything' },
];

const createEmptyRound = (order: number, seasonName: string): RoundDraft => ({
  roundNumber: order,
  theme: '',
  description: '',
  season: seasonName,
  submissionDeadline: '',
  votingDeadline: '',
});

const formatDateForInput = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const iso = date.toISOString();
  return iso.slice(0, 16);
};

const getRecommendedPreset = (playerCount: number) => {
  return scoringPresets.find((preset) => {
    const meetsMin = preset.minPlayers ? playerCount >= preset.minPlayers : true;
    const meetsMax = preset.maxPlayers ? playerCount <= preset.maxPlayers : true;
    return meetsMin && meetsMax;
  }) ?? scoringPresets[0];
};

const CreateSeason = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { state } = useStore();
  const { currentUser } = state;

  const [currentStep, setCurrentStep] = useState(1);
  const leagueId = searchParams.get('leagueId');
  const [leagueName, setLeagueName] = useState<string>('');
  const [isLoadingLeague, setIsLoadingLeague] = useState(true);
  const [seasonName, setSeasonName] = useState('');
  const [rounds, setRounds] = useState<RoundDraft[]>([createEmptyRound(1, '')]);
  const [hasCustomScoring, setHasCustomScoring] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState(() => {
    const preset = scoringPresets[0];
    return {
      maxPlayers: 12,
      scoringPresetId: preset.id,
      scoring: { ...preset.config },
      isPublic: false,
      requireSpotify: false,
    };
  });

  const resolvedRecommendedPreset = useMemo(
    () => getRecommendedPreset(formData.maxPlayers),
    [formData.maxPlayers]
  );

  useEffect(() => {
    if (!hasCustomScoring && resolvedRecommendedPreset.id !== formData.scoringPresetId) {
      setFormData((prev) => ({
        ...prev,
        scoringPresetId: resolvedRecommendedPreset.id,
        scoring: { ...resolvedRecommendedPreset.config },
      }));
    }
  }, [resolvedRecommendedPreset, hasCustomScoring, formData.scoringPresetId]);

  // Update rounds when season name changes
  useEffect(() => {
    if (seasonName) {
      setRounds((prev) => prev.map((r) => ({ ...r, season: seasonName })));
    }
  }, [seasonName]);

  // Fetch league info and verify user is admin
  useEffect(() => {
    if (!currentUser || !leagueId) {
      if (!leagueId) {
        toast({
          title: 'League required',
          description: 'Please navigate to a league and click "Create Season" from there.',
          variant: 'destructive',
        });
        navigate('/leagues');
      }
      return;
    }

    const fetchLeague = async () => {
      setIsLoadingLeague(true);
      try {
        // Verify user is admin of this league
        const adminLeagues = await api.leagueAPI.getLeaguesByAdmin(currentUser.id);
        const league = adminLeagues.find(l => l.id === leagueId);
        
        if (!league) {
          toast({
            title: 'Access denied',
            description: 'You must be an admin of this league to create seasons.',
            variant: 'destructive',
          });
          navigate('/leagues');
          return;
        }
        
        setLeagueName(league.name);
      } catch (error) {
        console.error('Failed to fetch league:', error);
        toast({
          title: 'Error',
          description: 'Failed to load league information. Please try again.',
          variant: 'destructive',
        });
        navigate('/leagues');
      } finally {
        setIsLoadingLeague(false);
      }
    };

    fetchLeague();
  }, [currentUser, leagueId, toast, navigate]);


  const handleAddRound = () => {
    setRounds((prev) => {
      const next = [...prev, createEmptyRound(prev.length + 1, seasonName)];
      return next.map((round, index) => ({ ...round, roundNumber: index + 1 }));
    });
  };

  const handleRemoveRound = (index: number) => {
    setRounds((prev) => {
      if (prev.length === 1) {
        toast({
          title: 'At least one round is required',
          variant: 'destructive',
        });
        return prev;
      }
      const next = prev.filter((_, idx) => idx !== index);
      return next.map((round, idx) => ({ ...round, roundNumber: idx + 1 }));
    });
  };

  const handleUpdateRound = <K extends keyof RoundDraft>(
    index: number,
    field: K,
    value: RoundDraft[K]
  ) => {
    setRounds((prev) =>
      prev.map((round, idx) =>
        idx === index ? { ...round, [field]: value } : round
      )
    );
  };

  const validateRounds = () => {
    if (!rounds.length) {
      toast({
        title: 'Add at least one round',
        description: 'You must define at least one round before creating the season.',
        variant: 'destructive',
      });
      return false;
    }

    for (const round of rounds) {
      if (!round.theme.trim()) {
        toast({
          title: 'Theme missing',
          description: `Round ${round.roundNumber} is missing a theme.`,
          variant: 'destructive',
        });
        return false;
      }
      if (!round.submissionDeadline || !round.votingDeadline) {
        toast({
          title: 'Missing deadline',
          description: `Round ${round.roundNumber} must include both submission and voting deadlines.`,
          variant: 'destructive',
        });
        return false;
      }
      const submission = new Date(round.submissionDeadline);
      const voting = new Date(round.votingDeadline);
      if (submission >= voting) {
        toast({
          title: 'Deadline conflict',
          description: `Voting deadline must be after submission deadline for round ${round.roundNumber}.`,
          variant: 'destructive',
        });
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!leagueId) {
      toast({
        title: 'League required',
        description: 'Please navigate to a league and click "Create Season" from there.',
        variant: 'destructive',
      });
      navigate('/leagues');
      return;
    }

    if (!seasonName.trim()) {
      toast({
        title: 'Season name required',
        variant: 'destructive',
      });
      return;
    }

    if (!validateRounds()) {
      return;
    }

    if (!currentUser) return;

    setIsSubmitting(true);

    try {
      // Create season with rounds
      const { season } = await api.seasonAPI.createSeason({
        league_id: leagueId!,
        season_name: seasonName.trim(),
        description: undefined,
        created_by: currentUser.id,
        max_players: formData.maxPlayers,
        scoring: formData.scoring,
        require_spotify: formData.requireSpotify,
        rounds: rounds.map((round, index) => ({
          theme: round.theme.trim(),
          description: round.description.trim() || undefined,
          season: seasonName.trim(),
          round_number: index + 1,
          submission_deadline: new Date(round.submissionDeadline).toISOString(),
          voting_deadline: new Date(round.votingDeadline).toISOString(),
          status: 'submitting' as const,
        })),
      });

      toast({
        title: 'Season created successfully! 🎉',
        description: `${seasonName} has been added to ${leagueName}. Invite code: ${season.invite_code}`,
      });
      navigate(`/leagues/${leagueId}`);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'We could not create your season. Please try again.';
      toast({
        title: 'Failed to create season',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (!leagueId) {
        toast({
          title: 'League required',
          description: 'Please navigate to a league and click "Create Season" from there.',
          variant: 'destructive',
        });
        navigate('/leagues');
        return;
      }
      if (!seasonName.trim()) {
        toast({
          title: 'Season name required',
          variant: 'destructive',
        });
        return;
      }
    }
    if (currentStep < steps.length) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  if (!currentUser) {
    return null;
  }

  const updateFormData = <K extends keyof typeof formData>(
    field: K,
    value: (typeof formData)[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            {/* League Display */}
            <div className="space-y-2">
              <Label>League</Label>
              {isLoadingLeague ? (
                <div className="text-sm text-muted-foreground">Loading league...</div>
              ) : (
                <div className="p-3 border rounded-lg bg-muted/50">
                  <p className="font-medium text-foreground">{leagueName}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Creating a new season for this league
                  </p>
                </div>
              )}
            </div>

            {/* Season Name */}
            <div className="space-y-2">
              <Label htmlFor="seasonName">Season Name *</Label>
              <Input
                id="seasonName"
                value={seasonName}
                onChange={(e) => setSeasonName(e.target.value)}
                placeholder="e.g., Fall 2024, Summer 2023, Winter 2025"
                maxLength={100}
              />
              <p className="text-xs text-muted-foreground">
                This name will be used for all rounds in this season.
              </p>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-8">
            <div className="space-y-2">
              <Label htmlFor="maxPlayers">Maximum Players *</Label>
              <Input
                id="maxPlayers"
                type="number"
                min="2"
                max="60"
                value={formData.maxPlayers}
                onChange={(e) =>
                  updateFormData('maxPlayers', Math.max(2, parseInt(e.target.value, 10) || 2))
                }
              />
              <p className="text-sm text-muted-foreground">
                The scoring preset below adjusts automatically based on this number.
              </p>
            </div>

            <div className="space-y-3">
              <Label>Scoring preset</Label>
              <div className="grid gap-3 md:grid-cols-2">
                {scoringPresets.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => {
                      updateFormData('scoringPresetId', preset.id);
                      updateFormData('scoring', { ...preset.config });
                      setHasCustomScoring(false);
                    }}
                    className={`text-left rounded-lg border p-4 transition ${
                      formData.scoringPresetId === preset.id
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <p className="font-semibold text-foreground">{preset.label}</p>
                    <p className="text-xs text-muted-foreground mt-1">{preset.description}</p>
                    {resolvedRecommendedPreset.id === preset.id && (
                      <Badge className="mt-3" variant="secondary">
                        Recommended
                      </Badge>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-lg border p-4 space-y-4">
              <div>
                <h3 className="font-semibold text-foreground">Fine-tune scoring</h3>
                <p className="text-sm text-muted-foreground">
                  Make changes after selecting a preset. We'll remember that you customised the values.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="upvotesPerPlayer">Max upvotes per player</Label>
                  <Input
                    id="upvotesPerPlayer"
                    type="number"
                    min="1"
                    value={formData.scoring.max_upvotes_per_player}
                    onChange={(e) => {
                      setHasCustomScoring(true);
                      updateFormData('scoring', {
                        ...formData.scoring,
                        max_upvotes_per_player: Math.max(1, parseInt(e.target.value, 10) || 1),
                      });
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="upvotesPerSong">Max upvotes per song</Label>
                  <Input
                    id="upvotesPerSong"
                    type="number"
                    min="1"
                    value={formData.scoring.max_upvotes_per_song}
                    onChange={(e) => {
                      setHasCustomScoring(true);
                      updateFormData('scoring', {
                        ...formData.scoring,
                        max_upvotes_per_song: Math.max(1, parseInt(e.target.value, 10) || 1),
                      });
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="downvotesPerPlayer">Max downvotes per player</Label>
                  <Input
                    id="downvotesPerPlayer"
                    type="number"
                    min="0"
                    value={formData.scoring.max_downvotes_per_player}
                    onChange={(e) => {
                      setHasCustomScoring(true);
                      updateFormData('scoring', {
                        ...formData.scoring,
                        max_downvotes_per_player: Math.max(0, parseInt(e.target.value, 10) || 0),
                      });
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="downvotesPerSong">Max downvotes per song</Label>
                  <Input
                    id="downvotesPerSong"
                    type="number"
                    min="0"
                    value={formData.scoring.max_downvotes_per_song}
                    onChange={(e) => {
                      setHasCustomScoring(true);
                      updateFormData('scoring', {
                        ...formData.scoring,
                        max_downvotes_per_song: Math.max(0, parseInt(e.target.value, 10) || 0),
                      });
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="requireSpotify"
                  checked={formData.requireSpotify}
                  onCheckedChange={(checked) => updateFormData('requireSpotify', checked === true)}
                />
                <Label htmlFor="requireSpotify" className="cursor-pointer">
                  Require Spotify links for submissions
                </Label>
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Rounds</h3>
                <p className="text-sm text-muted-foreground">
                  Define all rounds for this season. Each round needs a theme and deadlines.
                </p>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={downloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  Template
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = '.csv,.xlsx,.xls';
                  input.onchange = (e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (file) handleRoundsImport(file);
                  };
                  input.click();
                }}>
                  <Upload className="h-4 w-4 mr-2" />
                  Import
                </Button>
                <Button type="button" onClick={handleAddRound} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Round
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              {rounds.map((round, index) => (
                <Card key={index}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Round {round.roundNumber}</CardTitle>
                      {rounds.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveRound(index)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor={`theme-${index}`}>Theme *</Label>
                      <Input
                        id={`theme-${index}`}
                        value={round.theme}
                        onChange={(e) => handleUpdateRound(index, 'theme', e.target.value)}
                        placeholder="e.g., Songs that make you want to dance"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`description-${index}`}>Description</Label>
                      <Textarea
                        id={`description-${index}`}
                        value={round.description}
                        onChange={(e) => handleUpdateRound(index, 'description', e.target.value)}
                        placeholder="Optional description for this round"
                        rows={2}
                      />
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor={`submission-${index}`}>Submission Deadline *</Label>
                        <Input
                          id={`submission-${index}`}
                          type="datetime-local"
                          value={round.submissionDeadline}
                          onChange={(e) => handleUpdateRound(index, 'submissionDeadline', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`voting-${index}`}>Voting Deadline *</Label>
                        <Input
                          id={`voting-${index}`}
                          type="datetime-local"
                          value={round.votingDeadline}
                          onChange={(e) => handleUpdateRound(index, 'votingDeadline', e.target.value)}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Review Season Details</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-muted-foreground">League</Label>
                  <p className="font-medium">{leagueName}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Season Name</Label>
                  <p className="font-medium">{seasonName}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Maximum Players</Label>
                  <p className="font-medium">{formData.maxPlayers}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Scoring</Label>
                  <div className="mt-1 space-y-1 text-sm">
                    <p>Upvotes per player: {formData.scoring.max_upvotes_per_player}</p>
                    <p>Upvotes per song: {formData.scoring.max_upvotes_per_song}</p>
                    <p>Downvotes per player: {formData.scoring.max_downvotes_per_player}</p>
                    <p>Downvotes per song: {formData.scoring.max_downvotes_per_song}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Rounds</Label>
                  <p className="font-medium">{rounds.length} round{rounds.length !== 1 ? 's' : ''}</p>
                  <div className="mt-2 space-y-2">
                    {rounds.map((round, idx) => (
                      <div key={idx} className="text-sm p-2 bg-muted rounded">
                        <p className="font-medium">Round {round.roundNumber}: {round.theme}</p>
                        {round.description && <p className="text-muted-foreground">{round.description}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const downloadTemplate = () => {
    const headers = [
      'round_number',
      'theme',
      'description',
      'season',
      'submission_deadline',
      'voting_deadline',
    ];
    const sampleRow = [
      '1',
      'Songs that make you want to dance',
      'High-energy tracks that get everyone moving',
      seasonName || 'Season 1',
      '2025-01-01T12:00',
      '2025-01-03T12:00',
    ];
    const csvContent = [headers.join(','), sampleRow.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'beat-league-rounds-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const normaliseKey = (key: string) =>
    key
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');

  const handleRoundsImport = async (file: File) => {
    try {
      const extension = file.name.split('.').pop()?.toLowerCase();
      let rows: Record<string, unknown>[] = [];

      if (extension === 'csv') {
        const text = await file.text();
        const result = Papa.parse(text, { header: true, skipEmptyLines: true });
        if (result.errors.length) {
          throw new Error(result.errors[0].message);
        }
        rows = result.data as Record<string, unknown>[];
      } else if (extension === 'xlsx' || extension === 'xls') {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, { defval: '' });
      } else {
        throw new Error('Unsupported file type. Please upload a CSV or Excel file.');
      }

      if (!rows.length) {
        throw new Error('No rows found in the uploaded file.');
      }

      const importedRounds: RoundDraft[] = rows.map((row, index) => {
        const normalisedEntries = Object.entries(row).reduce<Record<string, unknown>>(
          (acc, [key, value]) => {
            if (key) {
              acc[normaliseKey(key)] = value;
            }
            return acc;
          },
          {}
        );

        const theme = String(normalisedEntries.theme || '').trim();
        const submission = String(normalisedEntries.submission_deadline || '').trim();
        const voting = String(normalisedEntries.voting_deadline || '').trim();

        if (!theme || !submission || !voting) {
          throw new Error(
            `Missing required fields on row ${index + 2}. Theme, submission deadline, and voting deadline are required.`
          );
        }

        const submissionISO = new Date(submission);
        const votingISO = new Date(voting);

        if (Number.isNaN(submissionISO.getTime()) || Number.isNaN(votingISO.getTime())) {
          throw new Error(
            `Invalid date format on row ${index + 2}. Use ISO format (YYYY-MM-DDTHH:mm).`
          );
        }

        if (submissionISO >= votingISO) {
          throw new Error(
            `Voting deadline must be after submission deadline (row ${index + 2}).`
          );
        }

        const season = String(normalisedEntries.season || seasonName || 'Season 1').trim();
        const description = String(normalisedEntries.description || '').trim();
        const roundNumberRaw = Number(normalisedEntries.round_number);
        const roundNumber = Number.isFinite(roundNumberRaw) && roundNumberRaw > 0
          ? Math.floor(roundNumberRaw)
          : index + 1;

        return {
          roundNumber,
          theme,
          description,
          season: season || seasonName,
          submissionDeadline: formatDateForInput(submissionISO.toISOString()),
          votingDeadline: formatDateForInput(votingISO.toISOString()),
        };
      });

      importedRounds.sort((a, b) => a.roundNumber - b.roundNumber);

      setRounds(
        importedRounds.map((round, idx) => ({
          ...round,
          roundNumber: idx + 1,
          season: seasonName || round.season,
        }))
      );

      toast({
        title: 'Rounds imported',
        description: `${importedRounds.length} rounds added from ${file.name}.`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to import rounds.';
      toast({
        title: 'Import failed',
        description: message,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Create Season</h1>
          <p className="text-muted-foreground">
            Add a new season to an existing league. Define rounds, scoring, and deadlines.
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Steps sidebar */}
          <div className="lg:col-span-1">
            <div className="space-y-4">
              {steps.map((step) => (
                <div
                  key={step.number}
                  className={`flex items-start gap-3 p-3 rounded-lg transition ${
                    currentStep === step.number
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/50'
                  }`}
                >
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
                      currentStep === step.number
                        ? 'bg-primary-foreground text-primary'
                        : 'bg-background text-muted-foreground'
                    }`}
                  >
                    {step.number}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${currentStep === step.number ? 'text-primary-foreground' : ''}`}>
                      {step.title}
                    </p>
                    <p className={`text-xs mt-0.5 ${currentStep === step.number ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Main content */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle>Step {currentStep}: {steps[currentStep - 1].title}</CardTitle>
              </CardHeader>
              <CardContent>
                {renderStepContent()}
                <div className="flex justify-between mt-8 pt-6 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBack}
                    disabled={currentStep === 1}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  {currentStep < steps.length ? (
                    <Button type="button" onClick={handleNext} className="btn-music">
                      Next
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={handleSubmit}
                      className="btn-music"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Calendar className="h-4 w-4 mr-2" />
                          Create Season
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CreateSeason;
